import { useCallback, useEffect, useState } from 'react';
import type { CallRecording } from '@stitch/pbx-adapter';
import { recordsApi, type RecordingTranscriptView } from '../api/client';
import { Button, Notice } from './ui';

/** "1:05" from seconds into the recording. */
const at = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/**
 * A call's recordings written out, in its full details: turn by turn (the caller, and the person
 * who took the call) when the recording keeps each side apart, else one block. Whoever may play a
 * recording may ask for it to be written out (Transcribe): it goes first in line, and this shows
 * how it's getting on until it's there. One waiting its turn to be written out automatically can
 * be asked for the same way (Transcribe now).
 */
export function RecordingTranscripts({ domain, record, recordings }: { domain: string; record: string; recordings: CallRecording[] }) {
  const [data, setData] = useState<{ transcripts: RecordingTranscriptView[]; canTranscribe: boolean } | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const [error, setError] = useState('');
  const load = useCallback(
    () =>
      recordsApi
        .callTranscripts(domain, record)
        .then(setData)
        .catch(() => setData(null)),
    [domain, record]
  );
  useEffect(() => {
    void load();
  }, [load]);
  // While one is being written out, look again every few seconds (every half minute while one only
  // waits its turn to be written out automatically, which can be tonight).
  const waits = data?.transcripts.filter((t) => t.status === 'pending') ?? [];
  const every = !waits.length ? 0 : waits.some((t) => !t.automatic || t.progress?.state === 'active') ? 3000 : 30_000;
  useEffect(() => {
    if (!every) return;
    const timer = setInterval(() => void load(), every);
    return () => clearInterval(timer);
  }, [every, load]);

  async function ask(id: string) {
    setAsking(id);
    setError('');
    try {
      await recordsApi.transcribe(domain, record, id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'It couldn’t be asked for.');
    } finally {
      setAsking(null);
    }
  }

  if (!data) return null;
  return (
    <div className="rec-transcripts">
      {data.transcripts.map((t) => {
        const rec = recordings.find((r) => r.id === t.recordingId);
        const label = recordings.length > 1 && rec ? <small className="muted">{rec.extensionName || rec.accountName || `Recording ${t.recordingId}`}</small> : null;
        if (t.status === 'done') {
          return (
            <section key={t.recordingId} className="transcript" aria-label="Transcript">
              {label}
              {t.turns?.length ? (
                <ol className="transcript-turns">
                  {t.turns.map((turn, i) => (
                    <li key={i} className={`turn turn-${turn.speaker ?? 'all'}`}>
                      <span className="turn-head">
                        {turn.name && <strong className="turn-who">{turn.name}</strong>}
                        <span className="turn-time">{at(turn.start)}</span>
                      </span>
                      <p>{turn.text}</p>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="muted">Nothing was said that could be written out.</p>
              )}
            </section>
          );
        }
        if (t.status === 'pending') {
          const p = t.progress;
          // Waiting its turn to be written out automatically: whoever may play it can have it now.
          if (t.automatic && p?.state !== 'active' && data.canTranscribe) {
            return (
              <div key={t.recordingId} className="transcript-ask">
                {label}
                <span className="muted">Waiting its turn to be written out.</span>
                <Button size="small" icon="spark" disabled={asking !== null} onClick={() => void ask(t.recordingId)}>
                  {asking === t.recordingId ? 'Asking…' : 'Transcribe now'}
                </Button>
              </div>
            );
          }
          return (
            <p key={t.recordingId} className="transcript-progress muted" role="status">
              {label} <span className="transcript-dot" aria-hidden="true" />
              {p?.state === 'active' ? 'Writing it out now…' : p && p.ahead > 0 ? `Waiting to be written out: ${p.ahead} ahead of it…` : 'Waiting to be written out…'}
            </p>
          );
        }
        if (!data.canTranscribe) return null;
        return (
          <div key={t.recordingId} className="transcript-ask">
            {label}
            {t.status === 'failed' && <span className="muted">It couldn’t be written out.</span>}
            <Button size="small" icon="spark" disabled={asking !== null} onClick={() => void ask(t.recordingId)}>
              {asking === t.recordingId ? 'Asking…' : t.status === 'failed' ? 'Try again' : 'Transcribe'}
            </Button>
          </div>
        );
      })}
      {error && <Notice tone="crit">{error}</Notice>}
    </div>
  );
}
